import React, { useState, useEffect, useCallback } from "react";
import { AxiosError } from "axios";
import { IEventSummary, IEventDocument } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";

import { Modal, useModal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import Confirm from "src/components/Confirm/Confirm";

import DocumentTile from "./DocumentTile";
import DocumentForm from "./DocumentForm";

import styles from "./DocumentsTab.module.scss";

interface IProps {
  event: IEventSummary;
  isAdmin: boolean;
}

const DocumentsTab: React.FC<IProps> = (props) => {
  const { isShown, toggle } = useModal();
  const [documents, setDocuments] = useState<IEventDocument[]>([]);
  const [editingDocument, setEditingDocument] = useState<IEventDocument | undefined>(undefined);
  const [deletingDocument, setDeletingDocument] = useState<IEventDocument | undefined>(undefined);
  const [deleting, setDeleting] = useState<boolean>(false);

  const getDocuments = useCallback((id: string) => {
    apiService.getEventDocuments(id).then((response: IEventDocument[]) => {
      setDocuments(response);
    });
  }, []);

  useEffect(() => {
    getDocuments(props.event._id);
  }, [props.event._id, getDocuments]);

  const onAddClick = () => {
    setEditingDocument(undefined);
    toggle();
  };

  const onEditClick = (doc: IEventDocument) => {
    setEditingDocument(doc);
    toggle();
  };

  const onDeleteConfirm = () => {
    if (!deletingDocument) return;

    setDeleting(true);
    apiService.deleteEventDocument(props.event._id, deletingDocument._id).then(() => {
      toast.show({
        title: "Delete Document",
        content: `Successfully removed ${deletingDocument.name}`,
        duration: 8000,
        type: "success"
      });
      setDeletingDocument(undefined);
      getDocuments(props.event._id);
    }).catch((e: AxiosError) => {
      toast.show({
        title: "Delete Document",
        content: "Unable to remove document",
        errorDetails: e,
        duration: 10000,
        type: "fail"
      });
    }).finally(() => {
      setDeleting(false);
    });
  };

  return (
    <div className={styles.DocumentsTab}>
      {props.isAdmin && (
        <div className={styles.actions}>
          <div className={styles.left}></div>
          <div className={styles.right}>
            <Button onClick={onAddClick}>Add Document</Button>
          </div>
        </div>
      )}
      <div className={styles.list}>
        {documents.length === 0 && <div className={styles.empty}>No documents added yet.</div>}
        {documents.map((doc) => (
          <DocumentTile
            key={doc._id}
            document={doc}
            isAdmin={props.isAdmin}
            onEdit={onEditClick}
            onDelete={(d) => setDeletingDocument(d)}
          />
        ))}
      </div>
      {props.isAdmin && (
        <Modal
          className={editingDocument ? "editDocumentModal" : "addDocumentModal"}
          isShown={isShown}
          hide={() => { toggle(); }}
          modalContent={
            <DocumentForm
              event={props.event}
              document={editingDocument}
              onFinish={() => { toggle(); getDocuments(props.event._id); }}
              onCancel={() => { toggle(); }}
            />
          }
        />
      )}
      {props.isAdmin && (
        <Modal
          className="Confirmation"
          isShown={!!deletingDocument}
          hide={() => { setDeletingDocument(undefined); }}
          modalContent={
            <Confirm
              message={`Are you sure you want to remove ${deletingDocument?.name}?`}
              submitting={deleting}
              buttonsDisabled={deleting}
              onCancel={() => { setDeletingDocument(undefined); }}
              onConfirm={onDeleteConfirm}
            />
          }
        />
      )}
    </div>
  );
};

export default DocumentsTab;
